import { useCallback, useEffect, useRef, useState } from "react"

export interface UseDelayedInputHandlerOptions {
  delay?: number
  handleChange: (value: string) => void
  value: string
}

export const useDelayedInputHandler = ({
  delay = 200,
  handleChange,
  value,
}: UseDelayedInputHandlerOptions) => {
  const [internalValue, setInternalValue] = useState(value)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastExternalValueRef = useRef(value)

  // Update internal value when external value changes (but not from our own changes)
  useEffect(() => {
    if (value !== lastExternalValueRef.current) {
      setInternalValue(value)
      lastExternalValueRef.current = value
    }
  }, [value])

  const debouncedHandleChange = useCallback(
    (newValue: string) => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        lastExternalValueRef.current = newValue
        handleChange(newValue)
      }, delay)
    },
    [handleChange, delay],
  )

  const immediateHandleChange = useCallback(
    (newValue: string) => {
      setInternalValue(newValue)
      debouncedHandleChange(newValue)
    },
    [debouncedHandleChange],
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    value: internalValue,
    handleChange: immediateHandleChange,
  }
}
